import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

const Frame = ({ title, subtitle, children, footer }) => (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="hidden flex-col justify-between bg-slate-900 p-10 text-white lg:flex">
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center bg-white text-[11px] font-bold text-slate-900">S3</span>
        <span className="font-display text-base font-semibold">Stock360</span>
      </div>
      <div>
        <h2 className="font-display text-3xl font-semibold leading-tight">
          Four numbers, everywhere.
        </h2>
        <div className="mt-8 grid grid-cols-2 gap-6 text-sm">
          {[["On Hand", "240", "#3B82F6"], ["Committed", "60", "#F59E0B"], ["Available", "180", "#10B981"], ["Incoming", "200", "#8B5CF6"]].map(
            ([label, value, color]) => (
              <div key={label}>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
                <p className="num text-2xl font-medium" style={{ color }}>{value}</p>
              </div>
            )
          )}
        </div>
      </div>
      <p className="text-xs text-slate-400">GBP pence-accurate · commits at invoice, never at quote</p>
    </div>
    <div className="flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <h1 className="font-display text-2xl font-semibold">{title}</h1>
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        {children}
        {footer}
      </div>
    </div>
  </div>
);

const field =
  "mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none transition-colors focus:border-slate-900 focus:ring-2 focus:ring-slate-200";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@stock360.co.uk");
  const [password, setPassword] = useState("Stock360!admin");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(email, password);
      toast.success("Signed in");
      navigate(user?.org?.onboarding_complete ? "/app" : "/onboarding");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame
      title="Sign in to Stock360"
      subtitle="Your inventory brain for Xero."
      footer={
        <p className="mt-6 text-sm text-slate-500">
          No account?{" "}
          <Link to="/register" className="text-slate-900 underline" data-testid="go-register-link">
            Start a 14-day trial
          </Link>
        </p>
      }
    >
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
        <label className="block text-sm">
          Email
          <input data-testid="login-email-input" className={field} value={email} onChange={(e) => setEmail(e.target.value)} required type="email" />
        </label>
        <label className="block text-sm">
          Password
          <input data-testid="login-password-input" className={field} value={password} onChange={(e) => setPassword(e.target.value)} required type="password" />
        </label>
        {error && <p className="bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit-button"
          className="w-full bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:opacity-50"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </Frame>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", org_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register(form);
      toast.success("Account created — let's connect Xero");
      navigate("/onboarding");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame
      title="Start your 14-day trial"
      subtitle="No card. Unlimited users."
      footer={
        <p className="mt-6 text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="text-slate-900 underline" data-testid="go-login-link">Sign in</Link>
        </p>
      }
    >
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
        <label className="block text-sm">
          Your name
          <input data-testid="register-name-input" className={field} value={form.name} onChange={set("name")} required />
        </label>
        <label className="block text-sm">
          Company
          <input data-testid="register-org-input" className={field} value={form.org_name} onChange={set("org_name")} required />
        </label>
        <label className="block text-sm">
          Work email
          <input data-testid="register-email-input" type="email" className={field} value={form.email} onChange={set("email")} required />
        </label>
        <label className="block text-sm">
          Password
          <input data-testid="register-password-input" type="password" minLength={8} className={field} value={form.password} onChange={set("password")} required />
          <span className="mt-1 block text-xs text-slate-500">At least 8 characters.</span>
        </label>
        {error && <p className="bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="register-error">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="register-submit-button"
          className="w-full bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create account"}
        </button>
      </form>
    </Frame>
  );
}
